import { ServiceName, ServiceNameType } from '@consigli/types';
import { type TFunction } from 'i18next';

import { type DirectoryServiceName } from '@/util/agents-catalogue';

export interface AgentDetailsFaq {
  question: string;
  answer?: string;
  /** A bulleted list under the answer, or in its place. */
  points?: string[];
}

export interface AgentDetailsImage {
  /** Undefined draws the frame's brand-light tile in the image's place. */
  src?: string;
  alt: string;
}

export interface AgentDetailsContent {
  /** One entry per paragraph. */
  about: string[];
  howItWorks: string[];
  currentScope: string[];
  faqs: AgentDetailsFaq[];
  images: AgentDetailsImage[];
  /** Draws the "Admin Only" pill after the tags. */
  adminOnly: boolean;
}

/** One agent's copy as each locale file carries it, under `agents-directory.content.<slug>`. */
interface AgentCopy {
  about: string[];
  'how-it-works': string[];
  'current-scope': string[];
  faqs: AgentDetailsFaq[];
}

export type AgentSlug = ServiceName | 'data-center';

/**
 * Each directory agent's slug: the `?agent=` value on `/agents`, the key of its
 * copy under `agents-directory.content` and `descriptions.*`, and its screenshot
 * folder.
 */
export const AGENT_CONTENT_SLUGS: Record<DirectoryServiceName, AgentSlug> = {
  [ServiceNameType.CODE_RESEARCH_AGENT]: ServiceName.CODE_RESEARCH_AGENT,
  // ServiceName is the `:serviceName` route vocabulary, and Data Center has no route there.
  [ServiceNameType.DATA_CENTER]: 'data-center',
  [ServiceNameType.DESIGN_PACKAGE_REVIEW]: ServiceName.DESIGN_PACKAGE_REVIEW,
  [ServiceNameType.GEOTECHNICAL_EXTRACTION_AGENT]: ServiceName.GEOTECHNICAL_EXTRACTION_AGENT,
  [ServiceNameType.LIGHTNING_DESIGNER]: ServiceName.LIGHTNING_DESIGNER,
  [ServiceNameType.LUMINAIRE_SCHEDULE]: ServiceName.LUMINAIRE_SCHEDULE,
  [ServiceNameType.MANAGEMENT_OPERATIONS_MAINTENANCE]:
    ServiceName.MANAGEMENT_OPERATIONS_MAINTENANCE,
  [ServiceNameType.MEETING_SECRETARY]: ServiceName.MEETING_SECRETARY,
  [ServiceNameType.OFFER]: ServiceName.OFFER,
  [ServiceNameType.QA_AGENT]: ServiceName.QA_AGENT,
  [ServiceNameType.RFP_RFQ_REVIEW]: ServiceName.RFP_RFQ_REVIEW,
  [ServiceNameType.SCHEDULE_CHECK]: ServiceName.SCHEDULE_CHECK,
  [ServiceNameType.SITE_DUE_DILIGENCE]: ServiceName.SITE_DUE_DILIGENCE,
  [ServiceNameType.SPACE_ANALYSIS]: ServiceName.SPACE_ANALYSIS,
  [ServiceNameType.TENDER_DOCUMENTS]: ServiceName.TENDER_DOCUMENTS,
  [ServiceNameType.VERSION_COMPARE]: ServiceName.VERSION_COMPARE,
  [ServiceNameType.WATER_PROCESS_SYSTEMS_AGENT]: ServiceName.WATER_PROCESS_SYSTEMS_AGENT,
};

const isDirectoryServiceName = (
  serviceName: ServiceNameType,
): serviceName is DirectoryServiceName => Object.hasOwn(AGENT_CONTENT_SLUGS, serviceName);

/** Undefined for an agent the directory does not list. */
export const agentSlug = (serviceName: ServiceNameType): AgentSlug | undefined =>
  isDirectoryServiceName(serviceName) ? AGENT_CONTENT_SLUGS[serviceName] : undefined;

/** The agent a `?agent=` value names, or undefined for any other string. */
export const agentForSlug = (slug: string): DirectoryServiceName | undefined =>
  (Object.keys(AGENT_CONTENT_SLUGS) as DirectoryServiceName[]).find(
    (serviceName) => AGENT_CONTENT_SLUGS[serviceName] === slug,
  );

/** i18next hands back the key itself on a miss, so anything but an object is no copy. */
const isAgentCopy = (value: unknown): value is AgentCopy =>
  typeof value === 'object' && value !== null && Array.isArray((value as AgentCopy).about);

/**
 * Each agent's gallery, one folder per slug and one numbered file per slide,
 * exported from the agent's section of the AI4AECOM Design System file.
 */
const SCREENSHOTS = import.meta.glob<string>('../../assets/images/agent-details/*/*.webp', {
  eager: true,
  import: 'default',
});

/** An agent with no folder gets no images, and the dialog then draws no gallery. */
const screenshots = (slug: string, t: TFunction): AgentDetailsImage[] =>
  Object.keys(SCREENSHOTS)
    .filter((path) => path.includes(`/agent-details/${slug}/`))
    .sort()
    .map((path, index) => ({
      src: SCREENSHOTS[path],
      alt: t('agents-directory.details.screenshot', { index: index + 1 }),
    }));

/**
 * The details copy for one agent in the reader's language, or null for an
 * agent the capability overview does not cover yet. The dialog then shows the
 * card's own description.
 */
export const agentDetailsContent = (
  serviceName: ServiceNameType,
  t: TFunction,
): AgentDetailsContent | null => {
  const slug = agentSlug(serviceName);
  if (slug === undefined) return null;

  const copy: unknown = t(`agents-directory.content.${slug}`, { returnObjects: true });
  if (!isAgentCopy(copy)) return null;

  return {
    about: copy.about,
    howItWorks: copy['how-it-works'],
    currentScope: copy['current-scope'],
    faqs: copy.faqs,
    images: screenshots(slug, t),
    adminOnly: false,
  };
};
